import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import useCheckIn from "../../data/useCheckIn";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ICheckInStep, ICreateUpdateCheckInStepRequest } from "../../models/CheckIn";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SwitchInput from "../shared/Inputs/SwitchInput";
import { TextInput } from "../shared/Inputs/TextInput";

type FormValues = {
  name: string,
  subtitle: string,
  order: number,
  needsValue: boolean,
  options: string,
  needsImage: boolean,
  isActive: boolean,
};

interface CheckInStepModalProps {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  checkInStep?: ICheckInStep;
  defaultOrder: number;
}

export default function CheckInStepModal({ open, setOpen, checkInStep, defaultOrder }: CheckInStepModalProps) {
  const { saveHandlers } = useDefaultCRUDHandlers("Check-In Step");
  const { deleteHandlers } = useDefaultCRUDHandlers("Check-In Step");
  const { createCheckInStep, updateCheckInStep, deleteCheckInStep } = useCheckIn();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>();

  const onSubmit = (data: FormValues) => {
    const updatedCheckInStep: ICreateUpdateCheckInStepRequest = {
      ...checkInStep,
      name: data.name,
      subtitle: data.subtitle?.trim() || null,
      order: Number(data.order),
      needsValue: data.needsValue,
      options: data.options?.trim() || null,
      needsImage: data.needsImage,
      isActive: data.isActive,
    };

    if (checkInStep !== undefined) {
      updateCheckInStep.mutate(updatedCheckInStep, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      createCheckInStep.mutate(updatedCheckInStep, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  };

  const handleDelete = () => {
    deleteCheckInStep.mutate(checkInStep?.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  };

  useEffect(() => {
    if (checkInStep !== undefined) {
      reset({
        name: checkInStep.name,
        subtitle: checkInStep.subtitle ?? "",
        order: checkInStep.order,
        needsValue: checkInStep.needsValue,
        options: checkInStep.options ?? "",
        needsImage: checkInStep.needsImage,
        isActive: checkInStep.isActive,
      });
    } else {
      reset({
        name: "",
        subtitle: "",
        order: defaultOrder,
        needsValue: false,
        options: "",
        needsImage: false,
        isActive: true,
      });
    }
  }, [checkInStep, open, reset, defaultOrder]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title="Delete Check-In Step" message="Are you sure you want to delete this check-in step? This action cannot be undone."
        confirmButtonText="Delete" onConfirm={handleDelete} isLoading={deleteCheckInStep.isLoading}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen} >
        <h1 className="text-xl">{`${checkInStep ? "Edit" : "Add"} a Check-In Step`}</h1>
        <h1 className="text-sm text-gray-500">All check-in steps will need to be ticked off but can optionally get a value, photograph or video.</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)} >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <TextInput label="Name" registerName="name" register={register} required={true} />
              <TextInput label="Subtitle" registerName="subtitle" register={register} />
              <input type="hidden" {...register("order")} />

              <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-3">
                <SwitchInput label="Needs Value" registerName="needsValue" register={register} setValue={setValue} watch={watch} />
                <SwitchInput label="Needs Image" registerName="needsImage" register={register} setValue={setValue} watch={watch} />
                <SwitchInput label="Is Active" registerName="isActive" register={register} setValue={setValue} watch={watch} />
              </div>

              <TextInput
                label="Options"
                registerName="options"
                register={register}
                placeholder="Alloy,Steel,Other"
              />
              <p className="text-sm text-gray-500">
                Leave options blank for free text, or enter comma-separated values for a dropdown.
              </p>

              <ButtonInput
                label={`${checkInStep ? "Save Changes" : "Create"}`}
                isSubmit={true}
                onClick={undefined}
                classes={undefined}
                isLoading={createCheckInStep.isLoading || updateCheckInStep.isLoading}
              />
              {checkInStep !== undefined && (
                <ButtonInput label="Delete" isSubmit={false} onClick={() => (setDeleteOpen(true))} classes="!bg-red-500 hover:!bg-red-600" />
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
